import React, { useEffect, useState, useContext } from "react";
import moment from "moment";
import H5 from "components/typography/H5";
import { Box, Divider, Flex, Grid, Label, Link, Spinner, Badge } from "theme-ui";
import { getQuoteRequestNotes, updateNote, createNote, deleteNote } from "api/Notes";
import { trackActivity } from "api/ActivityTracker";
import BasicButton from "components/forms/BasicButton";
import { PlusIcon } from "components/svg";
import { themevals } from "theme/themevals";
import P from "components/typography/P";
import AvatarIcon from "components/svg/icons/AvatarIcon";
import { EyeIcon, TrashIcon, NotesIcon, CheckMarkIcon, PencilIcon, QuestionMarkIcon } from "components/svg";
import UserContext from "context/User";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import SelectInput from "components/forms/SelectInput";
import TextInput from "components/forms/TextInput";
import { useToast } from "components/toast";
import BasicModal from "components/ui/BasicModal";

const Notes = ({ rfq, isCompany }: { rfq: any; isCompany: any }) => {
  const [notes, setNotes] = useState<any[] | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { user } = useContext<any>(UserContext);
  const [selectedNote, setSelectedNote] = useState<any>(null);
  const [canOpenEditModal, setCanOpenEditModal] = useState<boolean>(false);
  const [isAddOpen, setIsAddOpen] = useState<boolean>(false);
  const toast = useToast();
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState<boolean>(false);
  const [canOpenDeleteModal, setCanOpenDeleteModal] = useState<boolean>(false);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Notes";
    _init();
  }, []);

  useEffect(() => {
    if (canOpenEditModal && selectedNote) {
      setIsOpen(true);
      setCanOpenEditModal(false);
    }
  }, [canOpenEditModal, selectedNote, isOpen]);

  useEffect(() => {
    if (canOpenDeleteModal && selectedNote) {
      setConfirmDeleteModalOpen(true);
      setCanOpenDeleteModal(false);
    }
  }, [canOpenDeleteModal, selectedNote, confirmDeleteModalOpen]);

  const _init = async () => {
    const res = await getQuoteRequestNotes({ query: {}, data: { id: rfq.id } });
    if (res.items) {
      setNotes(res.items);
    }else{
        setNotes([]);
      }
  };

  const _submitNoteEdit = async (values: any) => {
    setSubmitting(true);

    const update = {
      note: values.note,
      private: values.private,
      entityType: "QuoteRequest",
      entityId: rfq.id,
    };

    const res = await updateNote({ data: { id: selectedNote.id }, update: update });

    if (res.id) {
      void trackActivity({
        module: "quote-requests",
        action: "edit_quote_request_note",
        actionType: "update",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "quote-request",
        entityId: rfq?.id,
        metadata: {
          noteId: selectedNote.id,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      setSubmitting(false);
      setIsOpen(false);
      setSelectedNote(null);
      toast.add("Note updated.", "var(--theme-ui-colors-success)");
      _init();
    } else {
      setSubmitting(false);
      toast.add("Error updating note.", "var(--theme-ui-colors-danger)");
    }
  };

  const _openEditModal = (note: any) => {
    setSelectedNote(note);
    setCanOpenEditModal(true);
    console.log(note);
  };

  const _cancelEditModal = () => {
    setSelectedNote(null);
    setCanOpenEditModal(false);
    setIsOpen(false);
  };

  const _cancelAddModal = () => {
    setIsAddOpen(false);
  };

  const _submitNoteAdd = async (values: any) => {
    setSubmitting(true);


    const noteData = {
      note: values.note,
      entityId: rfq?.id,
      entityType: "QuoteRequest",
      private: isCompany ? values.private : false,
    };
    
    
    const noteRes = await createNote({ data: noteData });
    if(noteRes.id) {
      // Track quote request note creation from quote notes component
      void trackActivity({
        module: "quote-requests",
        action: "add_quote_request_note",
        actionType: "create",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "quote-request",
        entityId: rfq?.id,
        metadata: {
          noteId: noteRes.id,
          source: "quote_notes",
          private: noteData.private,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      
      setSubmitting(false);
      setIsAddOpen(false);
      toast.add("Note added.", "var(--theme-ui-colors-success)");
      _init();
    }else{
        setSubmitting(false);
        toast.add("Error adding note.", "var(--theme-ui-colors-danger)");
    }
  };

  const _cancelConfirmModal = () => {
    setSubmitting(false);
    setConfirmDeleteModalOpen(false);
  };

  const _deleteNote = async () => {
    setSubmitting(true);
    const res = await deleteNote({ data: { id: selectedNote.id } });
    if(res.success){
        void trackActivity({
          module: "quote-requests",
          action: "delete_quote_request_note",
          actionType: "delete",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "quote-request",
          entityId: rfq?.id,
          metadata: {
            noteId: selectedNote.id,
          },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
        setSubmitting(false);
        setConfirmDeleteModalOpen(false);
        toast.add("Note deleted.", "var(--theme-ui-colors-success)");
        _init();
    }
  }

  const _openDeleteModal = (note: any) => {
    setSelectedNote(note);
    setCanOpenDeleteModal(true);
  };

  return (
    <>
      <H5 fontFamily="Rubik" fontSize="16px" marginLeft="0px" marginTop="0px">
        Notes
      </H5>
      <Flex sx={{ justifyContent: "flex-end" }}>
        <BasicButton scheme="secondaryOutline" onClick={() => setIsAddOpen(!isAddOpen)}>
          <PlusIcon fill={"#000"} />
          {"Add note"}
        </BasicButton>
      </Flex>
      <Flex sx={{ width: "100%", marginTop: "20px", flexDirection: "column" }}>
        {notes === null ? (
          <Flex sx={{ justifyContent: "center", alignItems: "center", width: "100%" }}>
            <Spinner size={24} />
          </Flex>
        ) : notes.length > 0 ? (
          notes.map((note, index) => {
            const logoUrl = note.lastUpdateBy?.data?.find((item: any) => item.key === "logo")?.value;
            return (
              <Flex
                key={index}
                sx={{
                  flexDirection: "column",
                  marginBottom: "20px",
                  borderRadius: "6px",
                  boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07);",
                  border: `1px solid ${themevals.colors.inputOutline}`,
                  width: "100%",
                }}
              >
                <Flex sx={{ padding: "20px 20px 30px 20px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
                  <Flex sx={{ fontFamily: "Poppins", fontSize: "14px", color: "#000", flex: 1, fontWeight:600 }}>{note?.note}</Flex>
                  <Flex sx={{ flex: 1, justifyContent: "flex-end", fontFamily: "Poppins", fontSize: "12px", color: themevals.colors.dkgrey }}>
                    {moment(note?.createdAt).format("h:mm a, dddd MMM Do, YYYY")}
                  </Flex>
                </Flex>
                <Flex sx={{ padding: "15px 20px" }}>
                  <Flex sx={{ flex: 1, justifyContent: "flex-start", alignItems: "center" }}>
                    <Flex sx={{ justifyContent: "flex-start", alignItems: "center", borderRadius: "6px", border: `1px solid ${themevals.colors.inputOutline}`, height: "36px", padding: "0px 8px" }}>
                      <Box sx={{ height: "20px", width: "20px" }}>
                        {logoUrl ? (
                          <img
                            src={logoUrl}
                            alt="Logo"
                            style={{
                              width: "20px",
                              height: "20px",
                              borderRadius: "4px",
                            }}
                          />
                        ) : (
                          <AvatarIcon fill={themevals.colors.dkgrey} width="20px" height="20px" />
                        )}
                      </Box>
                      <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="12px" color={themevals.colors.dkgrey} marginLeft="8px">
                        {note?.lastUpdateBy?.firstName} {note?.lastUpdateBy?.lastName}
                      </P>
                    </Flex>
                    {isCompany && (
                      <Flex sx={{ marginLeft: "30px", justfiyContent: "flex-start", alignItems: "center" }}>
                        <EyeIcon fill={themevals.colors.dkgrey} width="16px" />
                        <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="12px" marginLeft="8px" color={themevals.colors.dkgrey}>
                          {note?.private ? "Company Only" : "Client and Company"}
                        </P>
                      </Flex>
                    )}
                  </Flex>
                  {note?.lastUpdateBy?.id === user?.id && (
                    <Flex sx={{ flex: 1, justifyContent: "flex-end", alignItems: "center" }}>
                      <BasicButton scheme="secondaryOutline" onClick={() => _openEditModal(note)} styles={{ marginRight: "20px" }}>
                        <PencilIcon fill={"#000"} />
                        {"Edit"}
                      </BasicButton>
                      <BasicButton scheme="dangerSecondary" onClick={() => _openDeleteModal(note)}>
                        <TrashIcon fill={themevals.colors.danger} />
                        {"Delete"}
                      </BasicButton>
                    </Flex>
                  )}
                </Flex>
              </Flex>
            );
          })
        ) : (
          <Flex sx={{ justifyContent: "flex-start", alignItems: "center", width: "100%" }}>
            <P fontFamily="Poppins" fontSize="14px" color="gray">No notes available.</P>
          </Flex>
        )}
      </Flex>
      <Modal isOpen={isAddOpen} toggle={() => _cancelAddModal()} centered closeOnBackdropClick={false}>
        <Formik initialValues={{}} onSubmit={(values) => _submitNoteAdd(values)}>
          {({
            isSubmitting,
            values,
            setFieldValue,
            errors,
            submitCount,
          }: {
            isSubmitting: boolean;
            submitCount: number;
            values: any;
            setFieldValue: (name: string, val?: any) => void;
            errors: any;
          }) => {
            return (
              <Form noValidate autoComplete="off">
                <ModalHeader toggle={() => _cancelAddModal()}>
                  <ModalIcon>
                    <NotesIcon fill={"000"} />
                  </ModalIcon>
                  <ModalTitleWrapper>
                    <ModalTitle>Add Note</ModalTitle>
                    <ModalSubtitle>{"Add a note to the RFQ."}</ModalSubtitle>
                  </ModalTitleWrapper>
                </ModalHeader>
                <ModalBody>
                  <TextInput
                    name={`note`}
                    value={values.note}
                    type="textarea"
                    label="Note"
                    $customStyles={{ marginBottom: "30px" }}
                    onChange={(val: any) => setFieldValue(`note`, val)}
                    $errors={errors?.note && submitCount > 0 ? errors?.note : null}
                  />
                  {isCompany && (
                  <SelectInput
                    name="private"
                    label="Visibility"
                    value={values.private}
                    options={[
                      { value: false, label: "Client and Company" },
                      { value: true, label: "Company Only" },
                    ]}
                    onChange={(val: any) => setFieldValue("private", val)}
                    $errors={errors.private && submitCount > 0 ? errors.private : null}
                    $customStyles={{ width: "100%", marginBottom: "30px" }}
                  />
                )}
                </ModalBody>
                <ModalFooter>
                  <BasicButton type="button" onClick={() => setIsAddOpen(false)} scheme="secondaryOutline">
                    Cancel
                  </BasicButton>
                  <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true} styles={{ marginLeft: "10px" }}>
                    {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}Save Note
                  </BasicButton>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Modal>
      <Modal isOpen={isOpen} toggle={() => _cancelEditModal()} centered closeOnBackdropClick={false}>
        <Formik initialValues={{ note: selectedNote?.note, private: selectedNote?.private }} onSubmit={(values) => _submitNoteEdit(values)}>
          {({
            isSubmitting,
            values,
            setFieldValue,
            errors,
            submitCount,
          }: {
            isSubmitting: boolean;
            submitCount: number;
            values: any;
            setFieldValue: (name: string, val?: any) => void;
            errors: any;
          }) => {
            return (
              <Form noValidate autoComplete="off">
                <ModalHeader toggle={() => _cancelEditModal()}>
                  <ModalIcon>
                    <NotesIcon fill={"000"} />
                  </ModalIcon>
                  <ModalTitleWrapper>
                    <ModalTitle>Edit Note</ModalTitle>
                    <ModalSubtitle>{"Edit an existing note on the RFQ."}</ModalSubtitle>
                  </ModalTitleWrapper>
                </ModalHeader>
                <ModalBody>
                  <TextInput
                    name={`note`}
                    value={values.note}
                    type="textarea"
                    label="Note"
                    $customStyles={{ marginBottom: "30px" }}
                    onChange={(val: any) => setFieldValue(`note`, val)}
                    $errors={errors?.note && submitCount > 0 ? errors?.note : null}
                  />
                  {isCompany && (
                  <SelectInput
                    name="private"
                    label="Visibility"
                    value={values.private}
                    options={[
                      { value: false, label: "Client and Company" },
                      { value: true, label: "Company Only" },
                    ]}
                    onChange={(val: any) => setFieldValue("private", val)}
                    $errors={errors.private && submitCount > 0 ? errors.private : null}
                    $customStyles={{ width: "100%", marginBottom: "30px" }}
                  />
                )}
                </ModalBody>
                <ModalFooter>
                  <BasicButton type="button" onClick={() => setIsOpen(false)} scheme="secondaryOutline">
                    Cancel
                  </BasicButton>
                  <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true} styles={{ marginLeft: "10px" }}>
                    {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}Save Note
                  </BasicButton>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Modal>
      {confirmDeleteModalOpen && (
        <BasicModal
          title={`Delete Note?`}
          cancel_click={() => _cancelConfirmModal()}
          cancel_text="No, cancel"
          $submitting={submitting}
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="danger"
              onClick={() => _deleteNote()}
              $submitting={submitting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {!submitting && <TrashIcon fill={"#fff"} />}
              {"Delete note"}
            </BasicButton>
          }
        >
          <P lineHeight={"22px"}>Are you sure you want to delete this note?</P>
        </BasicModal>
      )}
    </>
  );
};

export default Notes;
